import { ApiMethod } from '@/enums/api-method'
import { Nullable } from '@/types'
import { Query } from '@//types/query'

type RecordParams = Record<string, any>

const getApiURL = (endpoint: string) => process.env.NEXT_PUBLIC_BACKEND_URL + endpoint

export class FetchError extends Error {
    status: number
    statusText: string
    url: string
    response: Response

    constructor(response: Response, message?: string) {
        super(message)
        this.response = response
        this.status = response.status
        this.statusText = response.statusText
        this.url = response.url
    }
}

export const fetchData = async <T = void>(
    url: string,
    method: ApiMethod = ApiMethod.GET,
    body: Nullable<RecordParams> = null,
    query: Nullable<Query> = null,
): Promise<T> => {
    const qs = Query.build(query)
    if (qs)
        url += `?${qs}`

    const args: RequestInit = {
        method: method,
        cache: 'no-cache',
        headers: {
            'X-Frontend-Host': typeof window !== "undefined" ? window?.location.href : '',
        }
    }

    if (body) {
        if (body instanceof FormData) {
            args.body = body
        }
        else {
            args.body = JSON.stringify(body)
            args.headers!['Content-type'] = 'application/json'
        }
    }

    let response = await fetch(new Request(url, args))
    if (response.status == 401)
        throw new FetchError(response, 'Unauthorized access')

    if (!response.ok) {
        let message = 'Neočekávaná chyba.'
        try {
            const data = await response.json()
            if ('message' in data)
                message = data['message']
            else if ('errorCode' in data)
                message = data['errorCode']
        }
        catch { }

        throw new FetchError(response, message)
    }

    const contentType = response.headers.get('content-type')
    if (contentType) {
        if (contentType.includes('application/octet-stream'))
            return response.blob() as T

        if (contentType.includes('application/json'))
            return response.json()
    }

    return response.text() as T
}

const createAPI = <GET = null, GETONE = null, POST = null, PUT = null, DEL = null>(
    route: string,
    getURL: (endpoint: string) => string = getApiURL,
): {
    getOne: (id: number | string | Query, queryParams?: Query) => Promise<GETONE>
    get: (idOrParams?: number | string | Query, queryParams?: Query) => Promise<GET>
    post: (idOrBody?: number | string | RecordParams, body?: RecordParams) => Promise<POST>
    put: (idOrBody?: number | string | RecordParams, body?: RecordParams) => Promise<PUT>
    delete: (idOrParams?: number | string | Query, queryParams?: Query) => Promise<DEL>
} => {
    const fetchWrapper = async <T>(method: ApiMethod, idOrParams?: number | string | Query, queryParams?: Query, body?: RecordParams) => {
        let url = getURL(route)

        if (idOrParams) {
            if (typeof idOrParams === 'string' || typeof idOrParams === 'number')
                url += `/${idOrParams}`
            else if (typeof idOrParams === 'object')
                return fetchData<T>(url, method, body, idOrParams)
        }

        return fetchData<T>(url, method, body, queryParams)
    }

    return {
        getOne: async (id: number | string | Query, queryParams?: Query) => fetchWrapper<GETONE>(ApiMethod.GET, id, queryParams),
        get: async (idOrParams?: number | string | Query, queryParams?: Query) => fetchWrapper<GET>(ApiMethod.GET, idOrParams, queryParams),
        post: async (idOrBody?: number | string | RecordParams, body?: RecordParams) => {
            switch (typeof idOrBody) {
                case 'number':
                case 'string':
                    return fetchWrapper<POST>(ApiMethod.POST, idOrBody, undefined, body)
            }

            return fetchWrapper<POST>(ApiMethod.POST, undefined, undefined, idOrBody)
        },
        put: async (idOrBody?: number | string | RecordParams, body?: RecordParams) => {
            switch (typeof idOrBody) {
                case 'number':
                case 'string':
                    return fetchWrapper<PUT>(ApiMethod.PUT, idOrBody, undefined, body)
            }

            return fetchWrapper<PUT>(ApiMethod.PUT, undefined, undefined, idOrBody)
        },
        delete: async (idOrParams?: number | string | Query, queryParams?: Query) => fetchWrapper<DEL>(ApiMethod.DELETE, idOrParams, queryParams),
    }
}

export default createAPI
